import React, { useMemo } from 'react';
import { type ISaleLabelProps, SaleLabel } from '@secretescapes/cauldron';
import { BadgeType, BadgeVariant } from '@secretescapes/falcon';
import { partition } from '@utils/lodash.utils';

interface IBadges {
  badges: ISaleLabelProps[];
}

function useBadgesForDisplay(badges: ISaleLabelProps[]) {
  return useMemo(() => {
    const [promoBadges, infoBadges] = partition(badges, ['type', BadgeType.PROMOTIONAL]);

    if (!promoBadges.length && !infoBadges.length) {
      return [];
    }

    const filteredBadges: ISaleLabelProps[] = [];
    const sortedPromoBadges = [...promoBadges].sort((a, b) => a.priority - b.priority);
    const exclusiveDealBadge = sortedPromoBadges.find(badge => badge.icon?.valueOf() === 'seMini');

    if (sortedPromoBadges.length > 0) {
      const firstSortedPromoBadge = sortedPromoBadges[0];
      filteredBadges.push(firstSortedPromoBadge);

      if (firstSortedPromoBadge.icon?.valueOf() !== 'seMini' && exclusiveDealBadge) {
        filteredBadges.push(exclusiveDealBadge);
      }
    }
    filteredBadges.push(...infoBadges);

    return filteredBadges;
  }, [badges]);
}

export const Badges = ({ badges }: IBadges) => {
  const filteredBadges = useBadgesForDisplay(badges);

  if (!filteredBadges.length) {
    return null;
  }

  return (
    <>
      {filteredBadges.map((badge, i) => {
        const { backgroundColor, icon, saleLabelTranslations, fontColor, priority, type } = badge;

        return (
          <SaleLabel
            disableTouch={false}
            key={`${saleLabelTranslations?.translatedLabelValue}_${icon}_${i}`}
            backgroundColor={backgroundColor}
            icon={icon}
            saleLabelTranslations={saleLabelTranslations}
            saleLabelVariant={BadgeVariant.normal}
            fontColor={fontColor}
            priority={priority}
            type={type}
            emboldened={type === BadgeType.INFORMATIONAL}
          />
        );
      })}
    </>
  );
};
